import React from "react";
import {
  AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  Callout, Checkbox, FieldGroup, FormField, Input, WhatsAppIcon,
} from "../../ui";
import { REMINDER_OPTIONS } from "./eventForm";

/**
 * EventAutomationSection — everything that goes out on its own: the WhatsApp
 * group link, the reminders before the start, and the follow-ups after the end.
 *
 * Reminders go by email and WhatsApp to everyone registered by the time each
 * one is due; someone who registers after a reminder's window opened is not
 * sent it — they just received the confirmation with the same link.
 */
const EventAutomationSection = ({ form, update, errors = {}, ended = false, noun = "webinar" }) => {
  const offsets = new Set(form.reminderOffsets || []);
  const toggleOffset = (minutes, on) => {
    const next = new Set(offsets);
    if (on) next.add(minutes); else next.delete(minutes);
    update({ reminderOffsets: [...next].sort((a, b) => b - a) });
  };
  const setFollowUp = (patch) => update({ followUp: { ...form.followUp, ...patch } });

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle>Reminders and follow-ups</AppCardTitle>
        <AppCardDescription>
          Sent automatically by email and WhatsApp, each with the attendee’s personal joining link.
        </AppCardDescription>
      </AppCardHeader>
      <AppCardContent className="tw-flex tw-flex-col tw-gap-6">
        <FormField
          label="WhatsApp group link (optional)"
          error={errors.whatsappGroupUrl}
          hint="Shown on the success screen and in the confirmation — never on the public page, so people register to get it."
        >
          <Input
            type="url"
            value={form.whatsappGroupUrl}
            leading={<WhatsAppIcon size={14} />}
            placeholder="https://chat.whatsapp.com/…"
            maxLength={300}
            onChange={(e) => update({ whatsappGroupUrl: e.target.value })}
          />
        </FormField>

        {!ended && (
          <FieldGroup legend="Reminders before it starts">
            <div className="tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
              {REMINDER_OPTIONS.map((o) => (
                <Checkbox
                  key={o.minutes}
                  checked={offsets.has(o.minutes)}
                  onChange={(e) => toggleOffset(o.minutes, e.target.checked)}
                  label={o.title}
                />
              ))}
            </div>
            {offsets.size === 0 && (
              <Callout tone="warning">No reminders will be sent. Most no-shows simply forgot — keep at least one.</Callout>
            )}
          </FieldGroup>
        )}

        <FieldGroup legend={`After the ${noun}`}>
          <Checkbox
            checked={form.followUp?.feedback !== false}
            onChange={(e) => setFollowUp({ feedback: e.target.checked })}
            label="Thank attendees and ask for feedback"
            description="About 15 minutes after the end, then two gentle reminders (next day, and 3 days later) to anyone who has not answered."
          />
          <Checkbox
            checked={!!form.followUp?.missedYou}
            onChange={(e) => setFollowUp({ missedYou: e.target.checked })}
            label="Send “Sorry we missed you” to registrants who didn’t join"
            description="About 2 hours after the end — with the recording, if you share one with attendees."
          />
        </FieldGroup>
      </AppCardContent>
    </AppCard>
  );
};

export default EventAutomationSection;
